
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { DomainRegistry } from "../../../domain";
import {
  Document,
  DocumentRegistrationResult,
  IDocumentRepository,
  DocumentEditBehavior,
  DocumentParameterContainer,
  SingleEntryCheckInformation,
  DocumentSummary,
} from "../../../domain/document";
import { EditingDocument } from "../editor";
import { DocumentEditViewBaseMixin } from ".";
import { MessageBroadcaster } from "../../system/utilities";
import { DateFormatter } from "../../../domain/system";

@Component
export default class DocumentEditViewMixin extends Mixins(DocumentEditViewBaseMixin) {
  data(): any {
    return {
      isRegistering: false,
      editTokenWithChainDocument: [],
    };
  }
  isRegistering: boolean;
  editTokenWithChainDocument: string[];

  async createNewInitializedDocument(patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, orderNo: string, systemKey: string, linkKey: string, parameterContainer: DocumentParameterContainer): Promise<Document> {
    const result = await this.createNewDocument(patientNo, formatId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey);
    await this.initializeNewDocument(result, orderNo, systemKey, linkKey, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, parameterContainer, true);
    return result;
  }

  async checkWritingLimitsAndCreateNewDocument(patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<Document> {
    if (!await this.isWritableFormat(formatId, patientNo)) { return null; }
    return this.createNewDocument(patientNo, formatId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey);
  }

  private async isWritableFormat(formatId: number, patientCode: string): Promise<boolean> {
    const isWritable = (await this.isWritingLimitOk(formatId)) && (await this.isSingleEntryCheckOk(patientCode, formatId));
    return isWritable;
  }

  private async isWritingLimitOk(formatId: number): Promise<boolean> {
    try {
      if (!await this.isNewDocumentCreatable(formatId)) {
        this.$suspend(
          "記載制限",
          "指定された" + this.$wording.format.name + "には記載制限が指定されています。");
        return false;
      }
      return true;
    } catch (e) {
      this.$fatal(
        "記載制限",
        "記載制限の取得に失敗しました。",
        e);
      return false;
    }
  }

  private async isSingleEntryCheckOk(patientCode: string, formatId: number): Promise<boolean> {
    if (!this.$configuration.behavior.document.editor.singleEntry.isEnable) { return true; }
    try {
      const repo = DomainRegistry.document.getDocumentRepository();
      const info = await repo.getSingleEntryCheckInformation(patientCode, formatId);
      if (info.editInformation) {
        // 記載している最中の文書がある
        await this.showEditAbortBySingleEntryDialog(info);
        this.$closeWindow(true);
        return false;
      }
      if (info.registeredDocumentSummary) {
        // 登録済み文書がある
        await this.showRegisteredDocumentEditConfirmDialog(info, patientCode);
        this.$closeWindow(true);
        return false;
      }
      return true;
    } catch (e) {
      this.$fatal("重複記載チェック", "重複記載情報の取得に失敗しました。", e);
    }
  }

  private async showRegisteredDocumentEditConfirmDialog(info: SingleEntryCheckInformation, patientCode: string): Promise<void> {
    try {
      const index = await this.$commonDialogs.confirmation.show({
        title: "重複記載",
        message: [
          "重複記載が禁止されたフォーマットです。",
          "既に記載済みの文書を修正しますか？",
          "",
          "記載日：" + DateFormatter.format(info.registeredDocumentSummary.createdDateTime, "YYYY/MM/DD HH:mm"),
          "記載者：" + info.registeredDocumentSummary.createStaffName
        ],
        buttons: [
          {
            kind: "confirm",
            label: "記載済みの文書を修正する",
          },
        ],
        cancellation: {
          isEnable: true,
          label: "画面を閉じる"
        },
      });
      if (index === 0) {
        this.$openWindow("/EditDocument", {
          patientNo: patientCode,
          documentId: info.registeredDocumentSummary.id.toString(),
        });
      }
    } catch (e) {
    }
  }

  private async showEditAbortBySingleEntryDialog(info: SingleEntryCheckInformation): Promise<void> {
    try {
      await this.$commonDialogs.confirmation.show({
        title: "重複記載",
        message: [
          "重複記載が禁止されたフォーマットです。",
          "現在別の記載中の文書があるため、記載を開始することができません。",
          "",
          "記載者：" + info.editInformation.userName,
          "端末名：" + info.editInformation.terminalName
        ],
        buttons: [],
        cancellation: {
          isEnable: true,
          label: "画面を閉じる"
        },
      });
    } catch (e) {
    }
  }

  async createNewDocument(patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<Document> {
    return this.createDocumentInstance(
      "新規文書",
      async r => {
        return r.getNewDocument(patientNo, formatId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey);
      });
  }

  async initializeNewDocument(document: Document, orderNo: string, systemKey: string, linkKey: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, parameterContainer: DocumentParameterContainer, shouldThrowError: boolean = false): Promise<void> {
    return this.initializeDocument(
      "新規文書",
      document,
      async i => {
        await i.initializeNewDocument(orderNo, systemKey, linkKey, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, parameterContainer, this.createBehavior());
        if (shouldThrowError && !i.isExternalScriptCompleted) { throw new Error("外部スクリプトでエラーが発生しています。"); }
      });
  }

  async loadInitializedExistingDocument(patientNo: string, documentId: number, orderNo: string, parameterContainer: DocumentParameterContainer): Promise<Document> {
    const result = await this.loadDocument(patientNo, documentId);
    await this.initializeExistingDocument(result, orderNo, parameterContainer);
    return result;
  }

  async loadDocument(patientNo: string, documentId: number, editToken?: string, writeToken?: string, ignoreFixKey?: string): Promise<Document> {
    return this.createDocumentInstance(
      "既存文書",
      async r => {
        return r.getDocument(patientNo, documentId, editToken, writeToken, ignoreFixKey);
      });
  }

  async initializeExistingDocument(document: Document, orderNo: string, parameterContainer: DocumentParameterContainer): Promise<void> {
    return this.initializeDocument(
      "既存文書",
      document,
      async i => {
        await i.initializeExistingDocument(orderNo, parameterContainer, this.createBehavior());
      });
  }

  async initializeCopiedDocument(document: Document, orderNo: string, parameterContainer: DocumentParameterContainer): Promise<void> {
    return this.initializeDocument(
      "コピー文書",
      document,
      async i => {
        if (this.$configuration.behavior.document.editor.externalTemplates.shouldSetCopiedIdWhenTemplateCopied) {
          const isTemplateIdCopySuccess = await i.copyExternalTemplateIds();
          if (!isTemplateIdCopySuccess) {
            this.$error("テンプレート", "テンプレートのコピーに失敗しました。");
          }
        }
        await i.initializeExistingDocument(orderNo, parameterContainer, this.createBehaviorForCopiedDocument());
      });
  }

  async getDocumentForQuotation(patientNo: string, documentId: number): Promise<Document> {
    return this.createDocumentInstance(
      "引用元既存文書",
      async r => {
        return r.getDocumentForQuotation(patientNo, documentId);
      });
  }

  async getDocumentForLatestQuotation(patientNo: string, formatId: number): Promise<Document> {
    return this.createDocumentInstance(
      "直近引用元既存文書",
      async r => {
        return r.getDocumentForLatestQuotation(patientNo, formatId);
      });
  }

  async loadNewCopiedDocument(patientNo: string, documentId: number): Promise<Document> {
    return this.createDocumentInstance(
      "新規コピー文書",
      async r => {
        return r.getNewCopiedDocument(patientNo, documentId);
      });
  }

  async loadAndValidateNewCopiedDocument(patientNo: string, documentId: number, isWorkflow: boolean): Promise<Document> {
    // 文書取得後だと記載トークンが作成されていて重複記載防止のメッセージを正しく表示できないため、文書取得前に判定するようにしている
    const summary = await this.getDocumentSummary(patientNo, documentId);
    if (!await this.isWritableFormat(summary.formatOriginalId, patientNo)) { return null; }

    const document = await this.createDocumentInstance(
      "新規コピー文書",
      async r => {
        return r.getNewCopiedDocument(patientNo, documentId);
      });
    const repository = DomainRegistry.format.getFormatRepository();
    const format = await repository.getFormatReference(document.attributes.format.originalId);
    if (!this.validateFormatWriteMethod(format, false)) { return null; }
    const latestFormat = await repository.getFormatStatus(document.attributes.format.originalId);
    if (!this.isEmbedded && this.$configuration.behavior.document.editor.formatUpdate.isEnable && latestFormat.id !== document.attributes.format.id) {
      this.$info(this.$wording.format.name + "更新可能", "文書の" + this.$wording.format.name + "が最新ではありません。必要であれば「" + this.$wording.format.name + "の更新」ボタンから更新して下さい。");
    }
    return document;
  }

  private async getDocumentSummary(patientNo: string, documentId: number): Promise<DocumentSummary> {
    const repository = DomainRegistry.document.getDocumentRepository();
    return repository.getDocumentSummary(patientNo, documentId);
  }

  private async isNewDocumentCreatable(formatOriginalId: number): Promise<boolean> {
    const repository = DomainRegistry.format.getFormatRepository();
    return repository.isNewDocumentCreatable(formatOriginalId);
  }

  async registerDocument(source: Document, isTemporary: boolean, isPublish: boolean): Promise<DocumentRegistrationResult> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      source.attributes.isTemporary = isTemporary;
      this.$logger.debug(source);
      this.isRegistering = true;
      await this.applyDocumentChain(source);
      if (this.shouldConfirmRegisterRevisedChainDocuments(source)) {
        const revisedChainDocuments = this.getRevisedChainDocuments(source);
        if (0 < revisedChainDocuments.length) {
          if (!await this.confirmRegisterRevisedChainDocuments(revisedChainDocuments)) {
            // 改版された文書は更新対象外としてchainDocumentsから省く.(バックエンド側で指定されてないものは元の文書と紐付ける.)
            await this.omitRevisedChainedDocuments(source, revisedChainDocuments);
          }
        }
      }
      const result = await repository.registerDocument(source);
      this.isRegistering = false;
      return result;
    } catch (e) {
      this.isRegistering = false;
      // メッセージは利用側で出す.
      if (isTemporary) {
        this.$error("一時保存失敗", "文書を一時保存できませんでした。", e);
      } else {
        this.$error("登録失敗", "文書を登録できませんでした。", e);
      }
      throw e;
    }
  }

  async registerChainedDocuments(source: Document, isTemporary: boolean, isPublish: boolean): Promise<DocumentRegistrationResult> {
    try {
      // 保守用なので連鎖先に直接改版された文書があるかどうかは確認しない
      this.isRegistering = true;

      source.attributes.isTemporary = isTemporary;
      await this.applyDocumentChain(source);

      if (source.editToken) {
        // 連鎖元文書の編集トークン。なんらかの理由で文書を登録できずにエラー画面に遷移したとき、閉じるボタンで文書編集トークンを削除するのに使用。
        this.editTokenWithChainDocument.push(source.editToken.token);
      }
      if (!source.chainDocuments || source.chainDocuments.length === 0) {
        this.isRegistering = false;
        throw new Error("連鎖先文書がありません。連鎖元文書Idを確認してください。");
      }
      // 連鎖先文書の編集トークン。なんらかの理由で文書を登録できずにエラー画面に遷移したとき、閉じるボタンで文書編集トークンを削除するのに使用。
      this.editTokenWithChainDocument = this.editTokenWithChainDocument.concat(
        Enumerable.from(source.chainDocuments).where(c => !!c.editToken).select(c => c.editToken.token).toArray()
      );

      const repository = DomainRegistry.document.getDocumentRepository();
      const result = await repository.registerChainedDocument(source);
      this.isRegistering = false;
      return result;
    } catch (e) {
      this.isRegistering = false;
      if (isTemporary) {
        this.$error("一時保存失敗", "文書を一時保存できませんでした。", e);
      } else {
        this.$error("登録失敗", "文書を登録できませんでした。", e);
      }
      throw e;
    }
  }

  // 終了時に過去一覧の再検索を行う
  async finishDocumentEditing(id: number, destinationRoute?: any, isTemporary?: boolean, archiveId?: number, isWindowClosing?: boolean): Promise<void> {
    this.notifyDocumentListMessage("documentRegistered");

    this.finishEditing(id, destinationRoute, isTemporary, archiveId, isWindowClosing);
  }

  notifyDocumentListMessage(key: string): void {
    try {
      const message = this.createPostMessage(key);
      if (window.opener) {
        window.opener.postMessage(message, "*");
      } else if (this.$configuration.behavior.localhost.window.opener.isEnable) {
        MessageBroadcaster.sendTo("ShowDocumentList", message);
        MessageBroadcaster.sendTo("ShowPatientDocumentList", message);
      }
    } catch (e) {
      console.log(e);
    }
  }

  private async applyDocumentChain(source: Document): Promise<void> {
    if (this.shouldChainDocuments(source)) {
      const chainFormatCount = (source.chainedFormatOriginalIds && (0 < source.chainedFormatOriginalIds.length))
        ? Enumerable.from(source.chainedFormatOriginalIds).count(i => Enumerable.from(source.chainableFormatOriginalIds).any(id => id === i))
        : source.chainableFormatOriginalIds.length;
      if (chainFormatCount > 0) {
        this.$logger.info("新規連鎖文書.連鎖先文書を作成します.(" + chainFormatCount + "文書)");
        await this.applyNewChainDocuments(source);
      }
    } else if (this.hasChainedDocuments(source)) {
      this.$logger.info("既存連鎖文書.連鎖先文書を作成します.(" + source.chainedDocuments.length + "文書)");
      await this.applyChainedDocuments(source);
    }
  }

  private async applyNewChainDocuments(source: Document): Promise<void> {
    await this.applyChainDocuments(
      source,
      (source.chainedFormatOriginalIds && (0 < source.chainedFormatOriginalIds.length))
        ? Enumerable.from(source.chainableFormatOriginalIds).where(i => Enumerable.from(source.chainedFormatOriginalIds).any(id => id === i)).toArray()
        : source.chainableFormatOriginalIds,
      (s, i) => this.createNewInitializedChainDocument(s.attributes.patient.id, i, s.chainedFormatOriginalIds));
  }

  async createNewInitializedChainDocument(patientNo: string, formatId: number, chainedFormatOriginalIds: number[], institutionNo: string = null, regionalInstitutionNo: string = null, hisInfoGetterParameter: string = null, orderNo: string = null, systemKey: string = null, linkKey: string = null, parameterContainer: DocumentParameterContainer = null) {
    const result = await this.createNewInitializedDocument(patientNo, formatId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, orderNo, systemKey, linkKey, parameterContainer);
    result.chainedFormatOriginalIds = chainedFormatOriginalIds;
    return result;
  }

  private async applyChainedDocuments(source: Document): Promise<void> {
    await this.applyChainDocuments(
      source,
      source.chainedDocuments,
      (s, i) => this.loadInitializedExistingDocument(s.attributes.patient.id, i.id, null, null));
  }

  private async omitRevisedChainedDocuments(source: Document, revisedChainDocuments: Document[]): Promise<void> {
    await this.applyChainDocuments(
      source,
      source.chainDocuments,
      (s, d) => this.toUnrevisedChainedDocument(d, revisedChainDocuments),
      revisedChainDocuments);
  }

  private async applyChainDocuments<T>(source: Document, keys: T[], create: (s: Document, k: T) => Promise<Document>, revisedChainDocuments: Document[] = null): Promise<void> {
    const results = await Promise.all(Enumerable.from(keys)
      .select(k => create(source, k))
      .where(d => !!d)
      .toArray());
    source.chainDocuments = await Promise.all(Enumerable.from(results)
      .where(d => !!d)
      .select(d => this.createMergedChainDocument(source, d, revisedChainDocuments))
      .toArray());
  }

  private async createMergedChainDocument<T>(source: Document, document: Document, revisedChainDocuments: Document[] = null): Promise<Document> {
    const result = await this.mergeDocument(document, source, true);
    result.attributes.isTemporary = source.attributes.isTemporary;
    const editor = new EditingDocument(result);
    await editor.initialize();
    if (revisedChainDocuments) {
      await this.omitRevisedChainedDocuments(result, revisedChainDocuments);
    } else {
      await this.applyDocumentChain(result);
    }
    return result;
  }

  async initializeNewExtraDocument(extraDocument: Document, document: Document): Promise<void> {
    return this.initializeDocument(
      "続紙",
      extraDocument,
      async i => {
        await i.initializeNewExtraDocument(document);
      },
      false);
  }

  private async createDocumentInstance(name: string, create: (r: IDocumentRepository) => Promise<Document>, shouldShowLoading: boolean = true): Promise<Document> {
    return this.createDocument(
      name,
      () => create(DomainRegistry.document.getDocumentRepository()),
      shouldShowLoading);
  }

  private createBehavior(): DocumentEditBehavior {
    const behavior = new DocumentEditBehavior();
    const shouldUpdateAndRollback = this.$configuration.behavior.document.editor.mischievousObjects.createDate.updateAndRollback.direct;
    behavior.shouldUpdateCreateDateOnEditStart = shouldUpdateAndRollback;
    behavior.shouldRollbackCreateDateOnTemporarySave = shouldUpdateAndRollback;
    behavior.shouldUpdateHisInformationOnEditStart = this.$configuration.behavior.document.editor.hisInformationUpdate.autoUpdateOnEditStart.direct;
    behavior.shouldUpdateHisInformationEmptyObjectOnly = this.$configuration.behavior.document.editor.hisInformationUpdate.shouldUpdateEmptyObjectOnlyOnEditStart.direct;
    behavior.isCommitPhase = true;
    behavior.enableButtonControl = true;
    behavior.shouldPrintSpecifiedSectionAtFirstRegistration = this.$configuration.behavior.document.editor.registerer.shouldPrintSpecifiedSectionAtFirstRegistration.direct;
    behavior.shouldIndicatePrintButtonForcibly = this.shouldIndicatePrintButtonForcibly;
    behavior.shouldUpdateAllHisItemsInExtraSectionWhenQuote = this.$configuration.behavior.document.editor.hisInformationUpdate.shouldUpdateAllHisItemsInExtraSectionWhenQuote.direct;
    behavior.shouldExecutePostRegistrationApplication = true;
    behavior.shouldFillDoctorAutomaticallyOnEditStart = this.$configuration.behavior.document.editor.mischievousObjects.doctor.fillAutomatically.direct;
    behavior.shouldCreateCollaborationSupplement = this.$configuration.behavior.document.editor.management.isEnable;  // 最終的にはcollaborationBehaviorのisManageableも含めて判定されます
    return behavior;
  }

  private createBehaviorForCopiedDocument(): DocumentEditBehavior {
    const behavior = new DocumentEditBehavior();
    behavior.shouldUpdateCreateDateOnEditStart = true;
    behavior.shouldRollbackCreateDateOnTemporarySave = false;
    behavior.shouldUpdateHisInformationOnEditStart = true;
    behavior.shouldUpdateHisInformationEmptyObjectOnly = false;
    behavior.isCommitPhase = true;
    behavior.enableButtonControl = true;
    behavior.shouldPrintSpecifiedSectionAtFirstRegistration = this.$configuration.behavior.document.editor.registerer.shouldPrintSpecifiedSectionAtFirstRegistration.direct;
    behavior.shouldIndicatePrintButtonForcibly = this.shouldIndicatePrintButtonForcibly;
    behavior.shouldUpdateAllHisItemsInExtraSectionWhenQuote = this.$configuration.behavior.document.editor.hisInformationUpdate.shouldUpdateAllHisItemsInExtraSectionWhenQuote.direct;
    behavior.shouldExecutePostRegistrationApplication = true;
    behavior.shouldFillDoctorAutomaticallyOnEditStart = this.$configuration.behavior.document.editor.mischievousObjects.doctor.fillAutomatically.direct;
    behavior.shouldCreateCollaborationSupplement = this.$configuration.behavior.document.editor.management.isEnable;  // 最終的にはcollaborationBehaviorのisManageableも含めて判定されます
    return behavior;
  }
}
